<template>
  <div>
    <h5 class="mb-4">{{ $t("meetingList") }}</h5>
    <div class="d-flex flex-row align-items-center justify-content-between mb-3">
      <b-form inline @submit.prevent="onClickSearchBtn">
        <b-form-input class="mr-2" size="sm" type="search" minlength="2" maxlength="200" v-model="searchText" :placeholder="$t('keyword')" />
        <b-form-select class="mr-2" size="sm" :options="filterStatusOptions" v-model="filterStatus" />
        <b-button type="submit" size="sm" variant="primary">{{ $t("search") }}</b-button>
      </b-form>

      <div class="d-flex flex-row align-items-center">
        <div v-if="totalItems" class="text-right flex-shrink-0 mr-3">{{ $t("resultCount", { count: totalItems }) }}</div>
        <b-pagination class="my-0 mr-3" size="sm" v-model="currentPage" :total-rows="totalItems" :per-page="itemsPerPage"></b-pagination>
        <b-form-select size="sm" v-model="itemsPerPage" :options="itemsPerPageSelections"></b-form-select>
      </div>
    </div>

    <b-table ref="table" :items="tableItemProvider" hover show-empty primary-key="id" :fields="tableFields"
             :per-page="itemsPerPage" :current-page="currentPage">
      <template v-slot:table-busy>
        <div class="text-center text-secondary py-4">{{ $t("loadingData") }}</div>
      </template>
      <template v-slot:empty>
        <div class="text-center text-secondary py-4">{{ $t("noData") }}</div>
      </template>

      <template v-slot:cell(name)="data">
        <router-link v-if="data.item.owner || isUserAdmin()" :to="`/meetings/details/${data.item.id}`">
          {{ data.item.name }}
        </router-link>
        <span v-else>{{ data.item.name }}</span>
        <br>
        <span class="mr-3" v-if="data.item.enableCamera" v-b-tooltip :title="$t('meetingShareCamera')">
          <b-icon-camera-video />
        </span>
        <span class="mr-3" v-else v-b-tooltip :title="$t('meetingTurnOffCamera')">
          <b-icon-camera-video-off />
        </span>
        <template v-if="isBBB(data.item)">
          <span class="mr-3" v-if="data.item.enableMuteOnStart" v-b-tooltip :title="$t('meetingMuteOnStart')">
            <b-icon-mic-mute />
          </span>
          <span class="mr-3" v-if="data.item.enablePrivateChat || data.item.enablePublicChat" v-b-tooltip
                :title="getMeetingChatSettingsTooltipText(data.item)">
              <b-icon-chat-dots />
            </span>
          <span class="mr-3" v-else v-b-tooltip :title="$t('meetingChatNotAllow')">
            <b-iconstack>
              <b-icon-chat />
              <b-icon-slash rotate="90" font-scale="2" shift-h="-4" shift-v="4" />
            </b-iconstack>
          </span>
        </template>
      </template>

      <template v-slot:cell(datetime)="data">
        <template v-if="data.item.actualStartTime && data.item.actualEndTime">
          {{ localDateFormat(data.item.actualStartTime) }}<br>
          {{ localTimeFormat(data.item.actualStartTime) }} - {{ localTimeFormat(data.item.actualEndTime) }}
        </template>
        <template v-else>
          {{ localDateFormat(data.item.scheduleStartTime) }}<br>
          {{ localTimeFormat(data.item.scheduleStartTime) }} - {{ localTimeFormat(data.item.scheduleEndTime) }}
        </template>
      </template>

      <template v-slot:cell(status)="data">
        <b-badge :variant="statusBadgeVariant[data.item.status]">{{ statusText[data.item.status] }}</b-badge>
      </template>

    </b-table>
  </div>
</template>

<style lang="scss" scoped>

</style>

<script lang="ts">
  import {Component, Ref, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import BaseComponent from "@/components/BaseComponent";
  import {BTable, BvTableFieldArray} from "bootstrap-vue";
  import {BvTableCtxObject, BvTableProviderPromiseResult} from "bootstrap-vue/src/components/table";
  import * as meetingService from "@/service/meetingService";

  @Component
  export default class CustomerMeetings extends BaseComponent {

    readonly statusText = {
      PENDING : this.$t("meetingStatusPending").toString(),
      INPROGRESS : this.$t("meetingStatusInProgress").toString(),
      END : this.$t("meetingStatusEnded").toString(),
      CANCEL : this.$t("meetingStatusCancelled").toString(),
    };

    readonly statusBadgeVariant = {
      PENDING : "warning",
      INPROGRESS : "success",
      END : "secondary",
      CANCEL : "danger"
    };

    searchText = "";
    filterStatus: string = null;
    readonly filterStatusOptions = [
      { value: null, text: this.$t("all").toString() },
      { value: "PENDING", text: this.$t("meetingStatusPending").toString() },
      { value: "INPROGRESS", text: this.$t("meetingStatusInProgress").toString() },
      { value: "END", text: this.$t("meetingStatusEnded").toString() },
      { value: "CANCEL", text: this.$t("meetingStatusCancelled").toString() },
    ];

    itemsPerPageSelections = [
      { value: 10, text: "10 " + this.$t("itemsPerPage").toString() },
      { value: 20, text: "20 " + this.$t("itemsPerPage").toString() },
      { value: 50, text: "50 " + this.$t("itemsPerPage").toString() },
      { value: 100, text: "100 " + this.$t("itemsPerPage").toString() },
    ];
    itemsPerPage = 20;
    currentPage = 1;
    totalItems = 0;

    tableFields: BvTableFieldArray = [
      {
        key: "id",
        label: "ID"
      },
      {
        key: "name",
        label: this.$t("meetingName").toString()
      },
      {
        key: "datetime",
        label: this.$t("datetime").toString(),
        thClass: "text-nowrap",
        tdClass: "text-nowrap"
      },
      {
        key: "status",
        label: this.$t("status").toString(),
        thClass: "text-nowrap",
        tdClass: "text-nowrap"
      },
      {
        key: "actions",
        label: "",
        thClass: "text-nowrap",
        tdClass: "text-nowrap"
      }
    ];

    @Ref("table") table: BTable;

    mounted() {

    }

    onClickReloadBtn() {
      this.searchText = "";
      this.filterStatus = null;
      this.currentPage = 1;

      this.table.refresh();
    }

    onClickSearchBtn() {
      this.table.refresh();
    }

    async tableItemProvider(ctx: BvTableCtxObject): Promise<BvTableProviderPromiseResult> {
      try {
        const res = await meetingService.getAllMeetingsByCustomer(this.$route.params.id, {
          q: this.searchText?.trim(),
          status: this.filterStatus,
          page: ctx.currentPage,
          pageSize: ctx.perPage
        });
        this.totalItems = res.total;
        return res.data;

      } catch (e) {
        this.toastError(e);
        return [];
      }
    }


    isBBB(meeting): boolean {
      return meeting?.corePlatform?.toLowerCase() === "bbb";
    }

    isZoom(meeting): boolean {
      return meeting?.corePlatform?.toLowerCase() === "zoom";
    }


    getMeetingChatSettingsTooltipText(meeting) {
      return [
        meeting.enablePrivateChat ? this.$t("meetingPrivateChatAllow").toString() : undefined,
        meeting.enablePublicChat ? this.$t("meetingPublicChatAllow").toString() : undefined,
      ].filter(_.identity).join("\n");
    }

  }
</script>
